import { useEffect, useId, useRef } from 'react';
import { Button } from './button';

// Brief: docs/plans/auth-design-brief.md §7 line 213 — destructive confirm
// dialogs (remove last 2FA method, regenerate codes, disable 2FA): panel-elev
// over scrim, max-width 400px, vertically centered, primary-action right-
// aligned. Cancel ghost; destructive primary uses `destructive` background.
//
// Layered above the side sheet (z-50 vs sheet's z-40) so it can appear on
// top when the user triggers a destructive action from inside a sheet.

interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', onKey);
    cancelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Cancel"
        tabIndex={-1}
        onClick={onCancel}
        className="absolute inset-0 cursor-default bg-background/85 motion-safe:animate-confirm-dialog-scrim-in"
      />
      <div className="relative w-full max-w-[400px] rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] motion-safe:animate-confirm-dialog-in">
        <div className="flex flex-col gap-3">
          <h2
            id={titleId}
            className="text-[16px] font-semibold leading-[1.3] tracking-[-0.01em] text-ink-high"
          >
            {title}
          </h2>
          <p id={bodyId} className="text-[14px] leading-[1.5] text-ink-mid">
            {body}
          </p>
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button ref={cancelRef} type="button" variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
